import React from 'react';
import { Popover } from 'antd';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import { handleImageUrl } from '$/utils/image';
import { RoleUploadMenu } from '../RoleMaterialRow';
import styles from './index.module.scss';

const ROLE_REFERENCE_THUMB_SIZE = 140;

interface RoleReferenceUploaderProps {
  imageUrl?: string;
  uploading?: boolean;
  dragActive?: boolean;
  uploadPopoverOpen?: boolean;
  popoverZIndex: number;
  onUploadPopoverOpenChange: (open: boolean) => void;
  onLocalUpload: () => void;
  onHistoryUpload: () => void;
  onDragEnter: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDrop: (event: React.DragEvent<HTMLButtonElement>) => void;
}

/**
 * 角色参考图上传区，支持点击上传、拖拽上传和已上传后的替换入口。
 *
 * @param props 见 RoleReferenceUploaderProps
 * @returns 参考图上传节点
 */
function RoleReferenceUploader({
  imageUrl,
  uploading,
  dragActive,
  uploadPopoverOpen,
  popoverZIndex,
  onUploadPopoverOpenChange,
  onLocalUpload,
  onHistoryUpload,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}: RoleReferenceUploaderProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const characterReferenceText = languageStore.smartcatTranslationText((value) => value.character_reference);
  const uploadingText = languageStore.smartcatTranslationText((value) => value.upload_progress);
  const uploadingStatusText = uploadingText.endsWith('...') ? uploadingText : `${uploadingText}...`;
  let uploadContent: React.ReactNode = (
    <div className={styles.referenceUploadInner}>
      <IconFont name="uploadBold" fontSize={20} color="currentColor" />
      <span>{languageStore.smartcatTranslationText((value) => value.click_or_drag_to_upload)}</span>
    </div>
  );

  if (imageUrl) {
    uploadContent = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={handleImageUrl(imageUrl, { size: ROLE_REFERENCE_THUMB_SIZE })}
        alt={characterReferenceText}
        crossOrigin="anonymous"
      />
    );
  }

  if (uploading) {
    uploadContent = (
      <div className={`${styles.referenceUploadInner} ${styles.referenceUploadLoading}`}>
        <IconFont
          name="loadingCircleBold"
          fontSize={18}
          color="currentColor"
          className={styles.referenceUploadLoadingIcon}
        />
        <span>{uploadingStatusText}</span>
      </div>
    );
  }
  const uploadMenu = <RoleUploadMenu onLocalUpload={onLocalUpload} onHistoryUpload={onHistoryUpload} />;
  const uploadButton = (
    <button
      type="button"
      className={`${styles.referenceUpload} ${dragActive ? styles.referenceUploadActive : ''} ${imageUrl && !uploading ? styles.referenceUploadPreview : ''}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      disabled={uploading}
    >
      {uploadContent}
      {imageUrl && !uploading && (
        <Popover
          trigger="click"
          placement="bottom"
          content={uploadMenu}
          overlayClassName={styles.referenceUploadPopover}
          zIndex={popoverZIndex}
          open={uploadPopoverOpen}
          onOpenChange={onUploadPopoverOpenChange}
        >
          <span className={styles.referenceReplaceBadge}>
            <IconFont name="uploadBold" fontSize={14} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((value) => value.replace)}</span>
          </span>
        </Popover>
      )}
    </button>
  );

  return (
    <div className={styles.referenceField}>
      <span className={styles.fieldLabel}>{uploading ? uploadingText : characterReferenceText}</span>
      {imageUrl || uploading ? uploadButton : (
        <Popover
          trigger="click"
          placement="bottomLeft"
          align={{ offset: [28, -70] }}
          content={uploadMenu}
          overlayClassName={styles.referenceUploadPopover}
          zIndex={popoverZIndex}
          open={uploadPopoverOpen}
          onOpenChange={onUploadPopoverOpenChange}
        >
          {uploadButton}
        </Popover>
      )}
    </div>
  );
}

export default RoleReferenceUploader;
